import type { InputState } from "../input/input";
import { los, norm } from "./physics";
import { tilesNow } from "./player";
import { restless } from "./reactions";
import { ENEMY_RADIUS, PLAYER_RADIUS, type Enemy, type WorldState } from "./types";

const WIND = 0.09;
const ACTIVE = 0.08;
const RECOVER_HIT = 0.14;
const RECOVER_MISS = 0.3;
const RANGE = 1.22;
const ARC = 1.05;
const DMG = 18;
const LUNGE = 3.6;

export function tickStrike(w: WorldState, dt: number, input: InputState): void {
  const p = w.player;
  if (p.strikePhase === "idle") {
    if (input.strike && p.stamina > 0.08) {
      const facing = p.facing.x || p.facing.y ? p.facing : { x: 0, y: 1 };
      p.strikePhase = "wind";
      p.strikeT = WIND;
      p.strikeLanded = false;
      p.stamina -= 0.08;
      p.vx += facing.x * LUNGE;
      p.vy += facing.y * LUNGE;
    }
    return;
  }
  p.strikeT -= dt;
  if (p.strikePhase === "wind" && p.strikeT <= 0) {
    p.strikePhase = "active";
    p.strikeT = ACTIVE;
    applyStrike(w);
  } else if (p.strikePhase === "active" && p.strikeT <= 0) {
    p.strikePhase = "recover";
    p.strikeT = p.strikeLanded ? RECOVER_HIT : RECOVER_MISS;
  } else if (p.strikePhase === "recover" && p.strikeT <= 0) {
    p.strikePhase = "idle";
  }
}

function applyStrike(w: WorldState): void {
  if (w.mode !== "hollow" || !w.hollow) return;
  const p = w.player;
  const map = tilesNow(w);
  const facing = p.facing.x || p.facing.y ? p.facing : { x: 0, y: 1 };
  let hit = false;
  for (const e of w.hollow.enemies) {
    if (e.state === "dead" || e.hp <= 0) continue;
    const dx = e.x - p.x;
    const dy = e.y - p.y;
    const d = Math.hypot(dx, dy);
    if (d > RANGE + ENEMY_RADIUS) continue;
    const dir = norm(dx, dy);
    const dot = dir.x * facing.x + dir.y * facing.y;
    if (dot < Math.cos(ARC)) continue;
    if (!los(map.tiles, map.w, map.h, p.x, p.y, e.x, e.y)) continue;
    hurtEnemy(w, e, DMG, facing);
    hit = true;
  }
  p.strikeLanded = hit;
  if (hit) w.shake = Math.max(w.shake, 0.4);
}

export function hurtEnemy(w: WorldState, e: Enemy, dmg: number, dir: { x: number; y: number }): void {
  e.hp -= dmg;
  e.stun = 0.34;
  e.vx += dir.x * 8.2;
  e.vy += dir.y * 8.2;
  e.state = e.hp <= 0 ? "dead" : "recover";
  w.memory.push({
    x: e.x,
    y: e.y,
    kind: "blood",
    age: 0,
    life: 1,
    hue: 0.02,
    layer: "hollow",
    depth: w.hollow?.depth,
  });
  if (e.hp <= 0) {
    e.state = "dead";
    w.memory.push({
      x: e.x,
      y: e.y,
      kind: "blood",
      age: 0,
      life: 1,
      hue: 0.08,
      layer: "hollow",
      depth: w.hollow?.depth,
    });
  }
}

export function hurtPlayer(w: WorldState, dmg: number, from: { x: number; y: number }): void {
  const p = w.player;
  if (p.invuln > 0) return;
  const bite = dmg * (1 + restless(w) * 0.5);
  p.hp -= bite;
  p.invuln = 0.52;
  p.injury = Math.min(1, p.injury + 0.08 * (1 + restless(w) * 0.6));
  const n = norm(p.x - from.x, p.y - from.y);
  p.vx += n.x * 6.8;
  p.vy += n.y * 6.8;
  w.shake = Math.max(w.shake, 0.46);
  w.memory.push({
    x: p.x,
    y: p.y,
    kind: "blood",
    age: 0,
    life: 1,
    hue: 0,
    layer: w.mode === "hollow" ? "hollow" : "overworld",
    depth: w.hollow?.depth,
  });
  w.home.blood = Math.min(1, w.home.blood + 0.04);
}

export function strikePct(w: WorldState): number {
  const p = w.player;
  if (p.strikePhase === "idle") return 1;
  const recover = p.strikeLanded ? RECOVER_HIT : RECOVER_MISS;
  const total = WIND + ACTIVE + recover;
  const left =
    p.strikeT +
    (p.strikePhase === "wind" ? ACTIVE + recover : p.strikePhase === "active" ? recover : 0);
  return 1 - left / total;
}

export function inMeleeReach(ax: number, ay: number, bx: number, by: number): boolean {
  return Math.hypot(ax - bx, ay - by) < RANGE + PLAYER_RADIUS;
}
